import { lazy } from '@loadable/component';
import { load } from '@porsche-design-system/components-js';
import { useContext, useEffect, useRef, Suspense } from 'react';
import { CompanyTheme } from '../api/types';
import { useRuntimeConfig } from '../runtimeConfig';
import ThemeComponentsContext from './context';

const PorscheTheme = lazy(() => import('./porsche'));
const PorscheV3Theme = lazy(() => import('./porscheV3'));
const SkodaTheme = lazy(() => import('./skoda'));
const VolkswagenTheme = lazy(() => import('./volkswagen'));
const CupraTheme = lazy(() => import('./cupra'));
const AdminTheme = lazy(() => import('./admin'));
const DefaultTheme = lazy(() => import('./default'));

export const useThemeComponents = () => {
    const context = useContext(ThemeComponentsContext);

    if (!context) {
        throw new Error('Missing ThemeProvider');
    }

    return context;
};

export type ThemeProviderProps = {
    children: React.ReactNode | JSX.Element;
    overrideTheme?: CompanyTheme;
};

const ThemeComponentsProvider = ({ children, overrideTheme }: ThemeProviderProps) => {
    const ThemeLoader = useThemeLoader(overrideTheme);

    const hasPorscheThemeLoaded = useRef(false);

    useEffect(() => {
        if (!hasPorscheThemeLoaded.current && ThemeLoader !== PorscheV3Theme) {
            load({ prefix: 'porsche' });
            hasPorscheThemeLoaded.current = true;
        }
    }, [ThemeLoader, overrideTheme]);

    return (
        <Suspense fallback={null}>
            <ThemeLoader>{children}</ThemeLoader>
        </Suspense>
    );
};

export default ThemeComponentsProvider;

const useThemeLoader = (overrideTheme?: CompanyTheme) => {
    const runtime = useRuntimeConfig();
    const theme = overrideTheme || runtime?.theme || CompanyTheme.Porsche;

    switch (theme) {
        case CompanyTheme.Volkswagen:
            return VolkswagenTheme;

        case CompanyTheme.Porsche:
            return PorscheTheme;

        case CompanyTheme.Admin:
            return AdminTheme;

        case CompanyTheme.Skoda:
            return SkodaTheme;

        case CompanyTheme.Default:
            return DefaultTheme;

        case CompanyTheme.PorscheV3:
            return PorscheV3Theme;

        case CompanyTheme.Cupra:
            return CupraTheme;

        default:
            throw new Error('Theme not implemented');
    }
};
